import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import classNames from 'classnames';
import { v4 } from 'uuid';
import { logger, download } from 'common/utils/helpers';
import { useGetDiagnosticPackageUrlMutation } from 'apis/dl';
import { addToastNotification } from 'redux/actions';
import { ReactTable } from 'components/shared/ReactTable';
import DiagnosticPackageDownloadButton from '../DiagnosticPackageDownloadButton';
import DiagnosticPackageTableHead from './DiagnosticPackageTableHead';
import DiagnosticPackageTableBody from './DiagnosticPackageTableBody';
import DiagnosticPackageTableRowGroup from './DiagnosticPackageTableRowGroup';
import DiagnosticPackageTableHeading from './DiagnosticPackageTableHeading';
import styles from './DiagnosticPackageTable.module.css';

function DiagnosticPackageTable({ data, downloadable, loading, retrievingId, retrieving, onComplete, onRetry }) {
  const dispatch = useDispatch();
  const { mutateAsync } = useGetDiagnosticPackageUrlMutation();
  const dateFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat('en-US', {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      }),
    []
  );
  const [receivedIds, setReceivedIds] = useState({});
  const handleDownload = useCallback(
    async ({ deviceId, diagPkgId, isDecrypted, createdTime }) => {
      const ext = isDecrypted ? '.zip' : '.tar.gz';
      const filename = `diagPackage-${deviceId}-${createdTime}${ext}`;

      try {
        const { downloadUrl } = await mutateAsync({ deviceId, diagPkgId, isDecrypted });

        download(downloadUrl, { filename });
      } catch (error) {
        logger.error(error);

        dispatch(
          addToastNotification({
            id: v4(),
            title: filename,
            description: 'failed to download.'
          })
        );
      }
    },
    [dispatch, mutateAsync]
  );
  const handleComplete = useCallback(
    (diagPkgId) => {
      onComplete(diagPkgId);

      if (diagPkgId) {
        setReceivedIds((prevState) => ({ ...prevState, [diagPkgId]: true }));
      }
    },
    [onComplete]
  );
  const handleView = useCallback((diagPkgId) => {
    setReceivedIds((prevState) => ({ ...prevState, [diagPkgId]: false }));
  }, []);

  const columns = useMemo(
    () =>
      [
        {
          id: 'createdTime',
          accessor: (diagPkg) => new Date(diagPkg.createdTime),
          maxWidth: 180,
          Header: 'Date/Time',
          Cell: ({ value }) => dateFormatter.format(value),
          className: styles.cell
        },
        {
          id: 'userName',
          accessor: (diagPkg) => diagPkg.userInfo?.name ?? '',
          Header: 'User',
          className: styles.cell
        },
        downloadable && {
          id: 'actions',
          sortable: false,
          resizable: false,
          maxWidth: 60,
          Cell: ({ original }) => (
            <DiagnosticPackageDownloadButton
              deviceId={original.deviceId}
              diagPkgId={original.diagPkgId}
              encrypted={original.encrypted}
              decrypted={original.decrypted}
              createdTime={original.createdTime}
              onDownload={handleDownload}
            />
          ),
          className: classNames(styles.cell, styles.cellCenter)
        }
      ].filter(Boolean),
    [handleDownload, dateFormatter, downloadable]
  );

  return (
    <ReactTable
      className={styles.root}
      loading={loading}
      data={data}
      columns={columns}
      showPagination={false}
      pageSize={Math.max(data.length, 10)}
      TheadComponent={DiagnosticPackageTableHead}
      TbodyComponent={DiagnosticPackageTableBody}
      TrGroupComponent={DiagnosticPackageTableRowGroup}
      ThComponent={DiagnosticPackageTableHeading}
      LoadingComponent={Loading}
      NoDataComponent={NoData}
      getTheadProps={() => ({
        diagPkgId: retrievingId,
        loading: loading || retrieving,
        onComplete: handleComplete,
        onRetry
      })}
      getTheadThProps={(props, _rowInfo, column) => ({
        sortable: column?.sortable ?? props.sortable
      })}
      getTrGroupProps={(_props, rowInfo) => ({
        diagPkgId: rowInfo?.original?.diagPkgId,
        received: receivedIds[rowInfo?.original?.diagPkgId] ?? false,
        onView: handleView
      })}
    />
  );
}

export default DiagnosticPackageTable;

function NoData() {
  return null;
}

function Loading() {
  return null;
}
